import React, { useState } from 'react';
import theme from '../styles/theme';
import styled from 'styled-components';

import { StyledText } from './index';
import { StyledTextVariantsTypeV2 } from './StyledText';

import { translate } from '../translations/translations';
import { isNotNullOrUndefined } from 'src/utils/helpers';
import { IPipeResult, pipe } from 'src/utils/validators/pipe';
import { IValidationKey, IValidatorErrorCode, IValidatorOptions } from 'src/utils/validators';

type InputType = 'text' | 'email' | 'password' | 'time';

interface IProps {
    id?: string;
    value?: number | string;
    defaultValue?: number | string;
    label?: string;
    placeholder?: string;
    height?: string;
    onChange?: (value: string) => void;
    validate?: (value: boolean) => void;
    required?: boolean;
    disabled?: boolean;
    width?: string;
    margin?: string;
    validators?: Array<IValidationKey | { key: IValidationKey; options: IValidatorOptions }>;
    type?: InputType;
}

const InputComponentContainer = styled.div<IProps>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    position: relative;
    width: ${(props) => (props.width ? props.width : '100%')};
    margin: ${(props) => (props.margin ? props.margin : 0)};
    height=auto;

    input {
        height: ${(props) => (props.height ? props.height : 'auto')};
        width: 100%;
        border: 1px solid ${theme.colors.light.grayscale['400']};
        color: ${theme.constantStyles.colorStyles.black};
        border-radius: 4px;
        box-sizing: border-box;
        background-color: ${theme.constantStyles.colorStyles.white};
        padding: 14px 20px;
        font-family: Mulish, sans-serif;
        font-size: 16px;
        font-weight: 400;
        &:disabled {
            opacity: 0.5;
        }

        &::placeholder {
            color: ${theme.colors.light.grayscale['400']};
        }
    }

    input:focus {
        outline: none;
        border: 1px solid ${theme.colors.light.green.baseLight};
    }

    input:hover {
        border: 1px solid ${theme.colors.light.grayscale['400']};
    }

    @media only screen and (max-width: 768px) {
        margin-bottom: 10px !important;
        width: 100%;
    }
`;

const InputWrapper = styled.div`
    position: relative;
    width: 100%;
    height: auto;

    i {
        position: absolute;
        top: 50%;
        right: 5%;
        -ms-transform: translateY(-50%);
        transform: translateY(-50%);
        color: ${theme.colors.light.grayscale['400']};
        cursor: pointer;
    }
`;

const LabelWrapper = styled.div`
    display: inline;
    margin: 0 0 10px 0;

    & > div,
    span {
        display: inline;
    }
`;

const LabelStar = styled.span`
    display: inline;
    color: red;
    margin-left: 5px;
    font-size: 18px;
    font-weight: bold;
`;

const Input = (props: IProps) => {
    const [focused, setFocused] = useState(false);
    const [value, setValue] = useState<string | number | null>('');
    const [passwordShown, setPasswordShown] = useState(false);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [validationError, setValidationError] = useState<
        Array<{
            key: IValidatorErrorCode;
            options?: IValidatorOptions;
        }>
    >();

    function onFocus() {
        setFocused(true);
    }

    function onBlur() {
        setFocused(false);
    }

    function validateValue(validationResult: IPipeResult) {
        if (props.validate) {
            props.validate(validationResult.isValid);
        }
        if (props.onChange) {
            props.onChange(validationResult.value);
        }
        setValidationError(validationResult.errors);
        setIsValid(validationResult.isValid);
    }

    function onChange(evt: React.ChangeEvent<HTMLInputElement>): void {
        const { validators } = props;
        setValue(evt.target.value);
        const validationResult = pipe(evt.target.value, validators);
        validateValue(validationResult);
    }

    function togglePasswordVisiblity(): void {
        setPasswordShown(!passwordShown);
    }

    return (
        <InputComponentContainer width={props.width} margin={props.margin} height={props.height}>
            {isNotNullOrUndefined(props.label) && (
                <LabelWrapper>
                    <StyledText text={props.label} variant={StyledTextVariantsTypeV2.PARAGRAPH_5} />
                    {props.required && <LabelStar>*</LabelStar>}
                </LabelWrapper>
            )}
            <InputWrapper>
                <input
                    id={props.id || 'inputValue'}
                    value={props.value || value?.toString()}
                    defaultValue={props?.defaultValue}
                    placeholder={props.placeholder}
                    height={props.height}
                    disabled={props.disabled}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    type={passwordShown ? 'text' : props.type}
                    style={{
                        borderBottomColor: !isValid
                            ? theme.colors.light.red.baseLight
                            : focused
                            ? theme.colors.light.green.baseLight
                            : theme.colors.light.grayscale['400'],
                    }}
                />
                {props.type === 'password' && <i className="fa fa-eye" onClick={togglePasswordVisiblity} />}
            </InputWrapper>
            {validationError && !isValid && (
                <StyledText
                    text={translate(`errors.${validationError[0].key}`)}
                    colorVariant={theme.colors.light.red.baseLight}
                    variant={StyledTextVariantsTypeV2.PARAGRAPH_4}
                    margin={'10px 0 0 0'}
                    width={'100%'}
                />
            )}
        </InputComponentContainer>
    );
};

export default Input;
